/*  portfolio_done_summary.ts
    – What the Global DPM team gets about a completed portfolio request, in one
      place so the "portfolio done" email (functions/src/index.ts onChangeService)
      and the "Global DPM export" button (components/global_dpm_export_button.tsx)
      show exactly the same fields. No firebase imports.
*/
import { Customer, RiskLevel, Service, TransferInvoice } from './types';

/** config/portfolio_service key of the Global DPM (multi-asset) service — the only one using the Cautious/Balanced/Growth naming. */
export const GLOBAL_DPM_PORTFOLIO_DB_ID = 'multi_assets';

const GLOBAL_DPM_RISK_LABELS: Record<string, string> = { low: 'Cautious', medium: 'Balanced', high: 'Growth' };

/** low/medium/high → the Global DPM service's level names; every other service keeps its raw value.
 *  Names come from config/portfolio_service.multi_assets.management_options[].risk_levels when the caller has them
 *  (webapp); the hardcoded map is the fallback (functions email, or config not loaded). */
export const riskToleranceLabel = (riskTolerance?: string, portfolio_db_id?: string, riskLevels?: RiskLevel[]): string => {
    const raw = (riskTolerance ?? '').trim();
    if (portfolio_db_id !== GLOBAL_DPM_PORTFOLIO_DB_ID) return raw;
    return riskLevels?.find((l) => l.id === raw.toLowerCase())?.name_en ?? GLOBAL_DPM_RISK_LABELS[raw.toLowerCase()] ?? raw;
};

/** Investment without fees, summed per currency: "10,000 KWD, 5,000 USD". Legacy invoices without initialAmount fall back to amount. */
export const investmentAmountText = (invoices?: TransferInvoice[]): string => {
    const byCurrency = new Map<string, number>();
    for (const inv of invoices ?? []) {
        const n = Number(inv.initialAmount ?? inv.amount) || 0;
        byCurrency.set(inv.currency, (byCurrency.get(inv.currency) ?? 0) + n);
    }
    return [...byCurrency.entries()].map(([ccy, n]) => `${n.toLocaleString('en-US')} ${ccy}`).join(', ');
};

/** Global DPM portfolios are USD only: the USD investment without fees as a number (0 when none). */
export const investmentAmountUsd = (invoices?: TransferInvoice[]): number =>
    (invoices ?? []).filter((inv) => inv.currency === 'USD').reduce((s, inv) => s + (Number(inv.initialAmount ?? inv.amount) || 0), 0);

/** Executed at = when Geneva confirmed the portfolio (geneva_responses.create_portfolio.timestamp); undefined while not there yet. */
export const executedAt = (service: Pick<Service, 'geneva_responses'>): Date | undefined => {
    const ts = service.geneva_responses?.create_portfolio?.timestamp as unknown;
    const d = ts instanceof Date ? ts : (ts as { toDate?: () => Date } | undefined)?.toDate?.();
    return d && !isNaN(d.getTime()) ? d : undefined;
};

/** executedAt as "dd/MM/yyyy HH:mm" Kuwait time (functions run in UTC); '' when not executed yet. */
export const executedDateText = (service: Pick<Service, 'geneva_responses'>): string => {
    const d = executedAt(service);
    if (!d) return '';
    return d.toLocaleString('en-GB', { timeZone: 'Asia/Kuwait', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '');
};

/** Link to the request page. host may be bare ("portal.kfhcapital.com.kw", as FUNCTIONS_URL is) or carry a scheme. */
export const portfolioRequestUrl = (host: string | undefined, serviceId: string): string =>
    `https://${(host ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '')}/portfolios/${serviceId}`;

/** Civil ID for individuals; commercial register for companies (and any other entity type). Either falls back to the other when its own is missing. */
export const customerIdNumber = (customer: Pick<Customer, 'customerType' | 'civilID' | 'commercial_register'> | undefined): string => {
    if (!customer) return '';
    const isIndividual = (customer.customerType ?? 'individual') === 'individual';
    return (isIndividual ? customer.civilID || customer.commercial_register : customer.commercial_register || customer.civilID) || '';
};

export interface PortfolioDoneSummary {
    name: string;
    idNumber: string; // civil ID or commercial register, see customerIdNumber
    portfolioId: string;
    amount: string; // per-currency text, for the email
    amountUsd: number; // numeric, for the excel (Global DPM is USD only)
    riskTolerance: string;
    link: string;
    executedDate: string; // formatted, for the email
    executedAt?: Date; // raw, for sorting / real date cells in the excel
}

/** The 7 fields, built identically for the email and the excel. */
export const portfolioDoneSummary = (
    service: Pick<Service, 'id' | 'portfolioOpeningRequest' | 'transferInvoices' | 'geneva_responses'>,
    customer: Pick<Customer, 'name_en' | 'name_ar' | 'customerType' | 'civilID' | 'commercial_register'> | undefined,
    host: string | undefined,
    riskLevels?: RiskLevel[],
): PortfolioDoneSummary => ({
    name: customer?.name_en || customer?.name_ar || '',
    idNumber: customerIdNumber(customer),
    portfolioId: service.portfolioOpeningRequest?.portfolioId ?? '',
    amount: investmentAmountText(service.transferInvoices),
    amountUsd: investmentAmountUsd(service.transferInvoices),
    riskTolerance: riskToleranceLabel(service.portfolioOpeningRequest?.riskTolerance, service.portfolioOpeningRequest?.portfolio_db_id, riskLevels),
    link: portfolioRequestUrl(host, service.id ?? ''),
    executedDate: executedDateText(service),
    executedAt: executedAt(service),
});
